import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GridFactory } from '../../../core/grid/services/grid.service';
import { UsersComponentComponent } from '../../../users-component/users-component.component';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';
import { AddUserModalComponent } from './add-user-modal/add-user-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

interface UserOption {
  LABEL: string;
  VALUE: string;
  checked: boolean;
}

@Component({
    selector: 'gtw-admin-users',
    templateUrl: './admin-users.component.html',
    styleUrls: ['./admin-users.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminUsersComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  clients: any[] = [];
  selectedClientKey: string = '';
  allUsersMode = false;
  loading = false;

  /** All Users multiselect state */
  allUserOptions: UserOption[] = [];
  filteredUserOptions: UserOption[] = [];
  userSearchText = '';
  userMenuOpen = false;
  loadingUsers = false;
  /**
   * Inline-style position for the multiselect dropdown.
   * Set from the trigger button's bounding rect on every open so the
   * `position: fixed` dropdown anchors under the button regardless of how
   * far down the user has scrolled the admin shell. See
   * `.gt-multiselect-dropdown` in `admin.styles.scss` for the rationale.
   */
  userMenuStyle: { [key: string]: string } = {};

  gridConfig!: AdminGridConfig;

  private readonly USERS_LIST_ACTION_ID = 40;
  private readonly USERS_LIST_GRID_ID = '3';
  private readonly ALL_USERS_ACTION_ID = 32994;
  private readonly ALL_USERS_GRID_ID = '6042';

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private appService: AppService,
    private globalService: GlobalService,
    private gridFactory: GridFactory,
    private dialog: MatDialog,
    private snackbar: GtwSnackbarService,
    private adminScreenContext: AdminScreenContextService
  ) {}

  ngOnInit(): void {
    this.allUsersMode = this.route.snapshot.data?.['allUsers'] === true;
    this.initClientData();

    const routePath = this.allUsersMode ? 'admin/all-users-list' : 'admin/users-list';

    if (this.allUsersMode) {
      this.gridConfig = {
        actionId: this.ALL_USERS_ACTION_ID,
        gridId: this.ALL_USERS_GRID_ID,
        extraParams: { client_key: this.selectedClientKey },
        autoLoad: false,
        cacheKey: routePath,
      };
      this.loadAllUsersList();
    } else {
      this.gridConfig = {
        actionId: this.USERS_LIST_ACTION_ID,
        gridId: this.USERS_LIST_GRID_ID,
        extraParams: { client_key: this.selectedClientKey },
        autoLoad: true,
        cacheKey: routePath,
      };
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.userMenuOpen) {
      this.userMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Close the multiselect on window scroll / resize so the `position: fixed`
   * panel doesn't visually detach from the trigger button. Reopen recomputes
   * the position from the current bounding rect.
   */
  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.userMenuOpen) {
      this.userMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    switch (event.type) {
      case 'form':
        if (event.callName === 'userDetail') {
          this.openUserEditModal(event.data);
        }
        break;
      default:
        if (event.callName === 'userDetail') {
          this.openUserEditModal(event.data);
        }
        break;
    }
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Filter / refresh actions
  // ---------------------------------------------------------------------------

  onClientChange(): void {
    this.gridConfig = {
      ...this.gridConfig,
      extraParams: { ...this.gridConfig.extraParams, client_key: this.selectedClientKey },
    };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  refresh(): void {
    if (this.allUsersMode) {
      this.loadAllUsersList();
    }
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh();
  }

  filterGrid(): void {
    const selectedSsos = this.allUserOptions
      .filter(u => u.checked)
      .map(u => u.VALUE);
    const overrides: Record<string, any> = { client_key: this.selectedClientKey };
    if (selectedSsos.length > 0) {
      overrides['sso_id_filter'] = selectedSsos.join(',');
    }
    this.adminGrid?.clearCache();
    this.adminGrid?.loadData(overrides);
  }

  addUser(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'admin-modal-panel';
    dialogConfig.width = '850px';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(AddUserModalComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (!result) {
        return;
      }
      if (result.action === 'editUser') {
        this.openUserEditModal({
          rowData: { SSO_ID: result.sso_id, CLIENT_KEY: result.client_key },
        });
      }
      this.adminGrid?.clearCache();
      this.adminGrid?.refresh();
    });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private initClientData(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.clients = userSettings.user.clients || [];
      const clientKey = userSettings.user.client?.client_key ?? userSettings.user.client_key;
      this.selectedClientKey = (clientKey != null) ? String(clientKey) : '';
    }
  }

  private loadAllUsersList(): void {
    this.loadingUsers = true;
    this.cdr.detectChanges();

    const url = `${environment.base_url}/loadGridJson?action_id=${this.USERS_LIST_ACTION_ID}&grid_id=${this.USERS_LIST_GRID_ID}`;
    this.gridFactory.loadGridData(url, {})
      .pipe(take(1))
      .subscribe(
        (data: any) => {
          if (data?.callSuccess === '1' && data.data) {
            this.allUserOptions = data.data.map((item: any) => ({
              LABEL: item.DISPLAY_NAME || `${item.FIRST_NAME || ''} ${item.LAST_NAME || ''}`.trim(),
              VALUE: item.SSO_ID || item.SSO || '',
              checked: false,
            }));
            this.filteredUserOptions = [...this.allUserOptions];
          }
          this.loadingUsers = false;
          this.cdr.detectChanges();
        },
        () => {
          this.loadingUsers = false;
          this.cdr.detectChanges();
        }
      );
  }

  private openUserEditModal(data: any): void {
    const sso = data?.rowData?.SSO_ID || data?.rowData?.SSO || data?.rowData?.user_id || data?.rowData?.sso_id;
    if (!sso) {
      this.snackbar.openSnackBar(
        'Cannot open user details: missing SSO_ID for the selected row.',
        'gtw-snackbar--red'
      );
      return;
    }
    const rowData = {
      user_id: sso,
      selected_client_key: data.rowData?.CLIENT_KEY ?? this.selectedClientKey,
      ...data.rowData,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['admin-user-edit-panel', 'increase-z-index'];
    dialogConfig.width = '90vw';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.position = { top: '20px' };
    dialogConfig.disableClose = true;
    dialogConfig.data = { rowData };

    this.adminScreenContext.setRowData(rowData);
    const dialogRef = this.dialog.open(UsersComponentComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.adminScreenContext.setRowData({});
      this.adminGrid?.clearCache();
      this.adminGrid?.refresh();
    });
  }

  // ---------------------------------------------------------------------------
  // All Users multiselect methods
  // ---------------------------------------------------------------------------

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
    if (this.userMenuOpen) {
      this.userMenuStyle = computeFixedDropdownPosition(event.currentTarget as HTMLElement);
    }
    this.cdr.detectChanges();
  }

  onUserSearch(): void {
    const term = (this.userSearchText || '').toLowerCase();
    this.filteredUserOptions = term
      ? this.allUserOptions.filter(u => u.LABEL.toLowerCase().includes(term) || u.VALUE.toLowerCase().includes(term))
      : [...this.allUserOptions];
    this.cdr.detectChanges();
  }

  toggleUserItem(user: UserOption, event: Event): void {
    event.stopPropagation();
    user.checked = !user.checked;
    this.cdr.detectChanges();
  }

  selectAllUsers(): void {
    this.filteredUserOptions.forEach(u => u.checked = true);
    this.cdr.detectChanges();
  }

  unselectAllUsers(): void {
    this.allUserOptions.forEach(u => u.checked = false);
    this.filteredUserOptions.forEach(u => u.checked = false);
    this.cdr.detectChanges();
  }

  get selectedUserCount(): number {
    return this.allUserOptions.filter(u => u.checked).length;
  }
}

/**
 * Build the inline-style object that anchors a `position: fixed`
 * `.gt-multiselect-dropdown` directly beneath a trigger button.
 *
 * Returns CSS values (`top`, `left`, `min-width`) derived from the
 * trigger's `getBoundingClientRect()`. The button's bottom edge becomes the
 * panel's top edge; widths match so the panel never appears narrower than
 * the button.
 *
 * Exported so any other admin filter that adopts the same `gt-multiselect`
 * visual (e.g. `AuditEngineComponent`'s user picker) can reuse this logic
 * without duplicating it.
 */
export function computeFixedDropdownPosition(trigger: HTMLElement | null): { [key: string]: string } {
  if (!trigger) return {};
  const rect = trigger.getBoundingClientRect();
  return {
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    'min-width': `${rect.width}px`,
  };
}
